import { hideOthers } from 'aria-hidden'
import {
  FC,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useState,
  type JSX,
} from 'react'
import FocusLock from 'react-focus-lock'
import { useCloseOnEscape, useOutsideClick } from 'shared-utilities'
import logo from '../../assets/qhack-2026-icon.svg'
import NavbarContent from '../../content/navbar'
import Button2026 from '../Button2026/Button2026'
import Dropdown from '../Dropdown/Dropdown'
import NavItem from './NavItem/NavItem'
import './Navbar.css'
import NavbarMobile from './NavbarMobile/NavbarMobile'

export interface ILinkComponentProps
  extends React.HTMLAttributes<HTMLAnchorElement> {
  children?: ReactNode | string
  href: string
  addClass?: string
  ariaLabel?: string
  id?: string
  tabIndex?: number
  openInCurrentTab?: boolean
  onClick?: () => void
  target?: string
}

export interface INavbarProps {
  LinkComponent: FC<ILinkComponentProps>
  RegisterComponent?: JSX.Element
  handle?: string
  isHandleLoading?: boolean
  isAuthenticated: boolean
  logIn: () => void
  signOut: () => void
  hasActiveEvent?: boolean
  pennyLaneBaseUrl: string
}

const Navbar = ({
  LinkComponent,
  RegisterComponent,
  handle,
  isHandleLoading = false,
  isAuthenticated,
  logIn,
  signOut,
  hasActiveEvent = true,
  pennyLaneBaseUrl,
}: INavbarProps) => {
  const [showMobileNavbar, setShowMobileNavbar] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  useLayoutEffect(() => {
    const onScroll = () => {
      setIsScrolled((window.scrollY || document.documentElement.scrollTop) > 0)
    }

    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  const [activeDropdownIndex, setActiveDropdownIndex] = useState<number | null>(
    null
  )
  const [showSignedInDropdown, setShowSignedInDropdown] = useState(false)
  const [currentDropdownTimeoutId, setCurrentDropdownTimeoutId] =
    useState<number>()

  const profileSlug = handle?.replace('@', '').trim()

  const loggedInDropdownItems = [
    {
      name: (
        <p>
          Account <i className="bx bx-link-external ml-2"></i>
        </p>
      ),
      href: `${pennyLaneBaseUrl}/account/`,
    },
    ...(profileSlug
      ? [
          {
            name: 'Profile',
            href: `${pennyLaneBaseUrl}/profile/${profileSlug}`,
          },
        ]
      : []),
    { name: 'Log out', itemOnClick: signOut },
  ]

  useCloseOnEscape(showMobileNavbar, setShowMobileNavbar)
  useCloseOnEscape(showSignedInDropdown, setShowSignedInDropdown)

  const ref = useOutsideClick(() => setShowSignedInDropdown(false))

  useEffect(() => {
    clearTimeout(currentDropdownTimeoutId)
    setCurrentDropdownTimeoutId(undefined)
  }, [activeDropdownIndex])

  const setTimeoutFunction = () => {
    const timer = window.setTimeout(() => {
      setActiveDropdownIndex(null)
      clearTimeout(timer)
    }, 500)
    setCurrentDropdownTimeoutId(timer)
  }

  // Toggle body overflow on mobile to eliminate scroll
  useEffect(() => {
    const bodyTag = document.querySelector('body')
    if (showMobileNavbar) {
      if (!bodyTag?.classList.contains('NavbarMobile--open')) {
        bodyTag?.classList.add('NavbarMobile--open')
      }
    } else {
      bodyTag?.classList.remove('NavbarMobile--open')
    }

    const Navbar = document.getElementById('navbar')

    // Hide outside elements from screen readers while in mobile menu
    if (showMobileNavbar) {
      hideOthers(Navbar as HTMLElement)
    } else {
      document.querySelectorAll('[aria-hidden="true"]').forEach((item) => {
        item.removeAttribute('aria-hidden')
      })
    }
  }, [showMobileNavbar])

  const accountLabel = handle || 'Account'

  const getButtonAriaLabel = () => {
    if (isHandleLoading) {
      return 'Loading account'
    }
    if (!isAuthenticated) {
      return 'Sign In'
    }
    if (!showSignedInDropdown) {
      return `${accountLabel} Show dropdown menu`
    }
    return `${accountLabel} Hide dropdown menu`
  }

  const authButtonLabel = isHandleLoading
    ? 'Loading…'
    : isAuthenticated
    ? handle || 'Account'
    : 'Sign In'

  const navbarBackgroundClass =
    isScrolled || showMobileNavbar ? ' Navbar--scrolled' : ''

  return (
    <div className="NavbarSticky">
      <FocusLock disabled={!showMobileNavbar}>
        <nav className={`Navbar${navbarBackgroundClass}`} id="navbar">
          <LinkComponent href="/">
            <img
              src={logo}
              className="NavbarLogo"
              alt="Qhack 2026"
              data-testid="nav-logo"
            />
          </LinkComponent>
          <button
            onClick={() => setShowMobileNavbar(!showMobileNavbar)}
            aria-controls="navbarSupportedContent"
            aria-label="Toggle navigation"
            className="MobileMenuToggle"
            aria-expanded={showMobileNavbar}
          >
            {showMobileNavbar ? (
              <i className="bx bx-x text-3xl"></i>
            ) : (
              <i className="bx bx-menu text-3xl"></i>
            )}
          </button>

          <ul className="Navbar__items" aria-hidden={showMobileNavbar}>
            {NavbarContent.navbarLeft.map((item, index) => {
              return (
                <NavItem
                  LinkComponent={LinkComponent}
                  key={`nav-link-${item.name}`}
                  navItem={item}
                  activeDropdownIndex={activeDropdownIndex}
                  setActiveDropdownIndex={setActiveDropdownIndex}
                  setTimeoutFunction={setTimeoutFunction}
                  index={index}
                  currentDropdownTimeoutId={currentDropdownTimeoutId}
                />
              )
            })}
          </ul>

          <div
            className={`Navbar--mobile ${
              showMobileNavbar ? `Navbar__items--open` : 'Navbar__items--closed'
            }`}
          >
            <NavbarMobile
              leftItems={NavbarContent.navbarLeft}
              LinkComponent={LinkComponent}
              setShowMobileNavbar={setShowMobileNavbar}
            />
          </div>

          <div className="Button__container" ref={ref}>
            {isAuthenticated && RegisterComponent && hasActiveEvent && (
              <div className="hidden tablet:block">{RegisterComponent}</div>
            )}
            {!hasActiveEvent ? null : (
              <div>
                <Button2026
                  variant="outlined"
                  onClick={
                    isHandleLoading
                      ? undefined
                      : isAuthenticated
                      ? () => setShowSignedInDropdown(!showSignedInDropdown)
                      : () => logIn()
                  }
                  className="AuthenticationButton"
                  data-testid="authentication-button"
                  aria-label={getButtonAriaLabel()}
                  aria-busy={isHandleLoading}
                  aria-expanded={showSignedInDropdown}
                >
                  {authButtonLabel}
                </Button2026>
                {isAuthenticated && showSignedInDropdown && (
                  <div className="SignedInDropdown">
                    <Dropdown
                      dropdownItems={loggedInDropdownItems}
                      LinkComponent={LinkComponent}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </nav>
      </FocusLock>
    </div>
  )
}

export default Navbar
